import {
  forwardRef,
  ForwardedRef,
  HTMLProps,
  ReactNode,
  SyntheticEvent,
  ReactElement,
} from 'react';

import {
  convertStyleObjectToCSSString,
  getDOMProps,
} from 'app/shared/utils/react';
import styled from 'styled-components';

import { FormControlLabel, Radio, RadioProps } from '@mui/material';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import {
  U21Tooltip,
  U21TooltipProps,
} from 'app/shared/u21-ui/components/display/U21Tooltip';

export type U21RadioValue = string | number | boolean;

export interface U21RadioProps<TValue extends U21RadioValue> extends Omit<
  HTMLProps<HTMLButtonElement>,
  'label' | 'onChange' | 'value'
> {
  checked?: boolean;
  description?: string;
  disabled?: boolean;
  ellipsis?: boolean;
  endIcon?: ReactElement;
  fullWidth?: boolean;
  inputProps?: RadioProps['inputProps'];
  label?: ReactNode;
  onChange: (value: TValue, e: SyntheticEvent) => void;
  tooltip?: string;
  tooltipProps?: Omit<U21TooltipProps, 'children' | 'tooltip'>;
  value: TValue;
}

export const U21RadioInner = <TValue extends U21RadioValue>(
  props: U21RadioProps<TValue>,
  ref: ForwardedRef<HTMLElement>,
) => {
  const {
    checked,
    description,
    disabled,
    ellipsis,
    endIcon,
    fullWidth,
    inputProps = {},
    label,
    onChange,
    tooltip,
    tooltipProps,
    value,
    ...rest
  } = props;

  const radioButton = (
    <Radio
      value={value}
      checked={checked}
      disabled={disabled}
      // e.target.value is based on the value prop which is typed as TValue
      onChange={(e) => onChange(e.target.value as TValue, e)}
      {...(label
        ? {}
        : {
            // HTMLButtonElement extends from HTMLElement so should be safe
            ref: ref as ForwardedRef<HTMLButtonElement>,
            ...getDOMProps(rest),
          })}
      inputProps={inputProps}
    />
  );

  if (!label) {
    if (tooltip && disabled) {
      return (
        <U21Tooltip tooltip={tooltip} {...tooltipProps}>
          <Span>{radioButton}</Span>
        </U21Tooltip>
      );
    }
    return (
      <U21Tooltip tooltip={tooltip} {...tooltipProps}>
        {radioButton}
      </U21Tooltip>
    );
  }

  return (
    <U21Tooltip tooltip={tooltip}>
      <StyledFormControlLabel
        $fullWidth={fullWidth}
        control={radioButton}
        disableTypography
        label={
          <LabelContainer horizontal useFlexGap>
            <LabelContent>
              {typeof label === 'string' ? (
                <U21Typography
                  color={disabled ? 'text.disabled' : 'text.primary'}
                  ellipsis={ellipsis}
                  variant="body2"
                >
                  {label}
                </U21Typography>
              ) : (
                label
              )}
              <U21Typography
                color="text.disabled"
                ellipsis={ellipsis}
                variant="body2"
              >
                {description}
              </U21Typography>
            </LabelContent>
            {endIcon}
          </LabelContainer>
        }
        ref={ref}
        {...getDOMProps(rest)}
      />
    </U21Tooltip>
  );
};

export const U21Radio = forwardRef(U21RadioInner);

const StyledFormControlLabel = styled(FormControlLabel)<{
  $fullWidth?: boolean;
}>`
  // ignore margin if full width since there won't be anything on the right
  margin-right: ${(props) => (props.$fullWidth ? 0 : 32)}px;
  // account for -11px margin-left default mui style
  width: ${(props) => (props.$fullWidth ? 'calc(100% + 11px)' : 'fit-content')};
  max-width: calc(100% + 11px);

  .MuiFormControlLabel-label {
    margin: 4px 0;
  }
`;

const LabelContainer = styled(U21Spacer)`
  flex: 1;
  overflow: hidden;
`;

const LabelContent = styled.div`
  overflow: hidden;
  margin: 4px 0;
  flex: 1;
  ${(props) => convertStyleObjectToCSSString(props.theme.typography.body2)};
`;

const Span = styled.span`
  display: inline-flex;
`;
